import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import { Button, Form, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { Loading } from "../../../common/loading/loading";
import { useUiFeedback } from "../../../../context/ui-feedback/ui-feedback";
import { createErrorFeedback } from "../../../../context/ui-feedback/action";
import {
  useDownloadAttachment,
  useLoadttachment,
} from "../../../../api/email-api";

export const EmailDetail = ({ email }) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const { uiFeedbackDispatch } = useUiFeedback();

  const [body, setBody] = useState(null);
  const [errorInBody, setErrorInBody] = useState(false);

  const downloadAttachment = useDownloadAttachment();
  const loadAttachment = useLoadttachment();

  useEffect(() => {
    const loadCidAttachments = async (cids) => {
      try {
        const promises = [];
        for (let cid in cids) {
          promises.push(
            new Promise((resolve, reject) => {
              loadAttachment(cids[cid].id, (data) => {
                if (data instanceof Error) {
                  reject(data);
                } else {
                  resolve({
                    cid: cids[cid].cid,
                    base64: data,
                  });
                }
              });
            }),
          );
        }

        await Promise.all(promises).then((values) => {
          let emailBody = email.message;
          values.forEach((item) => {
            emailBody = emailBody.replace(
              `src="cid:${item.cid}"`,
              `src="data:image/png;base64, ${item.base64}"`,
            );
          });
          setBody(emailBody);
        });
      } catch (ex) {
        if (ex.response?.status === 401 || ex.response?.status === 403) {
          navigate("/");
        } else {
          setErrorInBody(true);
          setBody(email.message);
        }
      }
    };

    const cids = email.attachments.filter((attachment) => attachment.cid);
    loadCidAttachments(cids);
  }, [intl, navigate, loadAttachment, uiFeedbackDispatch, email]);

  const download = async (attachmentId, atachmentName) => {
    try {
      await downloadAttachment(attachmentId, atachmentName, (data) => {
        if (data instanceof Error) {
          throw data;
        }
      });
    } catch (ex) {
      if (ex.response?.status === 401 || ex.response?.status === 403) {
        navigate("/");
      } else {
        createErrorFeedback(
          uiFeedbackDispatch,
          intl.formatMessage({
            id: "page.emails.exception.loadattachment.title",
          }),
          intl.formatMessage({
            id: "page.emails.exception.loadattachment.description",
          }),
        );
      }
    }
  };

  return (
    <>
      <Form.Group className="mb-3">
        <Form.Label>
          <FormattedMessage id="data.emails.form.to" />
        </Form.Label>
        <Form.Control
          type="text"
          value={email.to
            .filter((item) => !item.cc)
            .map((item) => `${item.name} (${item.address})`)
            .join(", ")}
          disabled={true}
        />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>
          <FormattedMessage id="data.emails.form.cc" />
        </Form.Label>
        <Form.Control
          type="text"
          value={email.to
            .filter((item) => item.cc)
            .map((item) => `${item.name} (${item.address})`)
            .join(", ")}
          disabled={true}
        />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>
          <FormattedMessage id="data.emails.form.subject" />
        </Form.Label>
        <Form.Control type="text" value={email.subject} disabled={true} />
      </Form.Group>
      <Form.Label>
        <FormattedMessage id="data.emails.form.content" />
      </Form.Label>
      <div>
        {errorInBody ? (
          <Alert variant="danger">
            <FormattedMessage id="page.emails.exception.loadbodyattachment" />
          </Alert>
        ) : null}
        {body ? (
          <div dangerouslySetInnerHTML={{ __html: body }}></div>
        ) : (
          <Loading />
        )}
      </div>
      <Form.Label>
        <FormattedMessage id="data.emails.form.attachments" />
      </Form.Label>
      <ul>
        {email.attachments
          .filter((attachment) => !attachment.cid)
          .map((attachment) => (
            <li key={attachment.id}>
              <Button
                variant="outline-primary"
                onClick={() => {
                  download(attachment.id, attachment.name);
                }}
              >
                {attachment.name}
              </Button>
            </li>
          ))}
      </ul>
    </>
  );
};

EmailDetail.propTypes = {
  email: PropTypes.object.isRequired,
};
